import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { LoggerContext } from './LoggerContext';

/**
 * Context for centralized error handling and display
 */
export const ErrorBoundaryContext = createContext({
	errors: [],
	addError: () => {},
	removeError: () => {},
	clearErrors: () => {},
});

/**
 * Custom hook to use the error boundary context
 * @returns {Object} Error boundary context
 */
export const useErrorBoundary = () => useContext(ErrorBoundaryContext);

/**
 * Provider component for the error boundary context
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components
 * @returns {JSX.Element} Provider component
 */
export const ErrorBoundaryProvider = ({ children }) => {
	const logger = useContext(LoggerContext);
	const [errors, setErrors] = useState([]);

	/**
	 * Add a new error to the error stack
	 * @param {string|Object} error - Error message or object
	 * @param {Object} options - Additional options for the error
	 * @param {string} options.id - Unique ID for the error
	 * @param {string} options.source - Source of the error
	 * @param {string} options.variant - Visual variant of the error
	 * @param {boolean} options.dismissible - Whether the error can be dismissed
	 * @param {number} options.autoHideDuration - Time in ms before auto-hiding
	 */
	const addError = useCallback((error, options = {}) => {
		const id = options.id || `error-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
		const errorObj = {
			id,
			message: typeof error === 'string' ? error : error?.message || 'An unknown error occurred',
			error: typeof error !== 'string' ? error : null,
			timestamp: new Date(),
			source: options.source || 'unknown',
			variant: options.variant || 'error',
			dismissible: options.dismissible !== undefined ? options.dismissible : true,
			autoHideDuration: options.autoHideDuration || 0,
		};

		// Log the error to the logger context if available
		if (logger?.logError) {
			logger.logError(
				errorObj.error || new Error(errorObj.message), 
				{ source: errorObj.source, errorId: id }
			);
		}

		setErrors(prev => [...prev, errorObj]);
	}, [logger]);

	/**
	 * Remove an error from the error stack
	 * @param {string} id - ID of the error to remove
	 */
	const removeError = useCallback((id) => {
		setErrors(prev => prev.filter(error => error.id !== id));
	}, []);

	/**
	 * Clear all errors
	 */
	const clearErrors = useCallback(() => {
		setErrors([]);
	}, []);

	/**
	 * Handle API errors
	 * This effect will monitor any window-level API error events
	 */
	useEffect(() => {
		const handleApiError = (event) => {
			if (event.detail?.error) {
				addError(event.detail.error, {
					source: event.detail.source || 'api',
					variant: 'error',
					dismissible: true,
				});
			}
		};

		// Listen for custom api-error events
		window.addEventListener('api-error', handleApiError);
		
		return () => {
			window.removeEventListener('api-error', handleApiError);
		};
	}, [addError]);

	const value = {
		errors,
		addError,
		removeError,
		clearErrors,
	};

	return (
		<ErrorBoundaryContext.Provider value={value}>
			{children}
		</ErrorBoundaryContext.Provider>
	);
};